<template>
    <v-popper ref="popper" position="bottomStart" :offset="4" @toggle="togglePopper" :disabled="disablePopperDropDown" v-fs-block>
        <div slot="trigger">
            <div class="field field-popper-select" :class="{'is-error': isInvalid, 'inline-s': disablePopperDropDown}">
                <v-inline-input
                    v-if="isPopperVisible"
                    :style="{width: `${buttonWidth}px`}"
                    @input="updateSearchFilter"
                    :auto-focus="true"/>
                <r-button
                    v-show="!isPopperVisible"
                    ref="button"
                    type="link"
                    v-popper-trigger
                    class="no-flex cursor-pointer btn-dropdown-link"
                    :class="{'cursor-default': disablePopperDropDown}">
                    {{ valuePreview }}
                </r-button>
                <v-icon
                    v-show="!isPopperVisible && !disablePopperDropDown"
                    v-on:click="openPopper"
                    class="icon-gray"
                    icon="caret-down-s"/>
            </div>
        </div>
        <div class="popover" slot="content">
            <div class="popover-control" :class="{'is-scrollable has-l-height': computedOptions.length > 7}">
                <div class="popover-content popover-menu">
                    <template v-if="hasOptions">
                        <a
                            v-for="option in computedOptions"
                            class="popover-menu-item"
                            href="#"
                            @click.prevent="updateSelection(option.value)">
                            {{ option.label | capitalize }}
                        </a>
                    </template>
                    <p v-else class="inset-s">No elements found. Consider changing the search query.</p>
                </div>
            </div>
        </div>
    </v-popper>
</template>

<script>
    import vIcon from './v-icon';
    import vPopper from '../v-popper';
    import vInlineInput from '@/components/form/v-inline-input';

    export default {
        name: 'v-popover-select',
        components: {
            vInlineInput,
            vIcon,
            vPopper,
        },
        props: {
            label: {
                type: String,
                default: null,
            },
            value: {
                type: [String, Number, Boolean],
                default: null,
            },
            options: {
                type: Array,
                required: true,
            },
            validate: {
                type: Object,
                default: null,
            },
            preSelectFirst: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            /**
             *
             * TODO: implement v-popper-select shortcuts
             * after https://github.com/Rebilly/frontend-recomm/pull/672
             *
             */
            return {
                filter: '',
                buttonWidth: null,
                isPopperVisible: false,
            };
        },
        computed: {
            computedOptions() {
                const isPrimitiveOptions = typeof this.options[0] !== 'object';
                const options = isPrimitiveOptions
                    ? this.options.map(value => ({value, label: value}))
                    : this.options;

                const filter = this.filter ? this.filter.toLowerCase() : false;
                return filter
                    ? options.filter(({value, label}) => (
                        String(value).toLowerCase().includes(filter) || String(label).toLowerCase().includes(filter)
                    ))
                    : options;
            },
            hasOptions() {
                return !!(this.computedOptions.length);
            },
            hasOnlyOneOption() {
                return this.hasOptions && this.computedOptions.length === 1;
            },
            disablePopperDropDown() {
                if (!this.hasOnlyOneOption || !this.value) {
                    return false;
                }
                const [option] = this.computedOptions;
                return option.value === this.value;
            },
            valuePreview() {
                if (!this.value && this.label) {
                    return this.label;
                }
                const option = this.computedOptions.find(o => o.value === this.value);
                return option ? option.label || option.value : this.value || this.label;
            },
            isInvalid() {
                if (this.validate) {
                    return this.validate.$invalid && this.validate.$dirty;
                }
                return false;
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.calcButtonWidth();
            });
        },
        methods: {
            calcButtonWidth() {
                if (this.$refs.button) {
                    const min = 35;
                    const max = 150;
                    const button = this.$refs.button.$el.offsetWidth;
                    const current = this.buttonWidth;

                    const values = [button, current].filter(v => v && v > min && v < max);
                    // set width as minimal item in list
                    this.buttonWidth = values.length ? Math.min.apply(null, values) : min;
                }
            },
            updateSelection(value) {
                this.$emit('input', value);
            },
            updateSearchFilter(value) {
                this.filter = value;
                this.$emit('search-change', value);
            },
            selectFirst() {
                if (!this.value && this.preSelectFirst) {
                    const {value} = this.options[0];
                    this.$emit('input', value);
                }
            },
            openPopper() {
                this.$refs.popper.$emit('request-popper-show');
            },
            togglePopper(toggle) {
                this.isPopperVisible = toggle ? !this.isPopperVisible : this.isPopperVisible;
                this.$nextTick(() => {
                    this.filter = '';
                    // when popper is hidden calculate button width
                    if (!this.isPopperVisible) {
                        this.calcButtonWidth();
                    }
                });
            },
        },
        created() {
            this.selectFirst();
        },
    };
</script>

<docs>
    Default (pre select first by default is true)
    ```vue
    <template>
        <div>
            <v-popover-select
                label="Select a value"
                :value="value"
                :options="options"
                @input="updateValue"/>
        </div>
    </template>
    <script>
        export default {
            data() {
                return {
                    value: '',
                    options: new Array(10).fill(0).map((n, i) => ({value: `value-${i}`, label: `label-${i}`})),
                };
            },
            methods: {
                updateValue(value) {
                    this.value = value;
                },
            },
        };
    </script>
    ```

    Default (pre select first is false)
    ```vue
    <template>
        <div>
            <v-popover-select
                label="Select a value"
                :pre-select-first="false"
                :value="value"
                :options="options"
                @input="updateValue"/>
        </div>
    </template>
    <script>
        export default {
            data() {
                return {
                    value: '',
                    options: new Array(10).fill(0).map((n, i) => ({value: `value-${i}`, label: `label-${i}`})),
                };
            },
            methods: {
                updateValue(value) {
                    this.value = value;
                },
            },
        };
    </script>
    ```
</docs>
